import { Component, computed, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core/i18n';
import { TenantService } from '../../../../core/tenant/tenant.service';
import { BillingFacade } from '../../store/billing.facade';

@Component({
  selector: 'app-trial-badge',
  standalone: true,
  imports: [RouterLink, LucideAngularModule],
  template: `
    @if (isVisible()) {
      <a class="trial-badge" [attr.data-color]="color()" [routerLink]="billingLink()">
        <lucide-icon name="clock" [size]="12" aria-hidden="true"></lucide-icon>
        <span>{{ label() }}</span>
      </a>
    }
  `,
  styles: [
    `
      :host { display: contents; }

      .trial-badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        height: 26px;
        padding: 0 10px;
        border-radius: 999px;
        font-size: 11.5px;
        font-weight: 600;
        letter-spacing: 0.01em;
        text-decoration: none;
        white-space: nowrap;
        border: 1px solid transparent;
        transition: transform 0.15s ease, filter 0.15s ease;
      }

      .trial-badge:hover { transform: translateY(-1px); filter: brightness(1.05); }

      .trial-badge[data-color='blue'] {
        background: color-mix(in srgb, #3b82f6 15%, transparent);
        color: #1d4ed8;
        border-color: color-mix(in srgb, #3b82f6 28%, transparent);
      }
      .trial-badge[data-color='yellow'] {
        background: color-mix(in srgb, #eab308 18%, transparent);
        color: #a16207;
        border-color: color-mix(in srgb, #eab308 32%, transparent);
      }
      .trial-badge[data-color='orange'] {
        background: color-mix(in srgb, #f97316 20%, transparent);
        color: #c2410c;
        border-color: color-mix(in srgb, #f97316 34%, transparent);
      }
      .trial-badge[data-color='red'] {
        background: color-mix(in srgb, #ef4444 22%, transparent);
        color: #b91c1c;
        border-color: color-mix(in srgb, #ef4444 38%, transparent);
      }
    `,
  ],
})
export class TrialBadgeComponent {
  private readonly facade = inject(BillingFacade);
  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);

  readonly t = this.i18n.t.bind(this.i18n);

  readonly isVisible = computed(() => this.facade.isTrialing());
  readonly color = computed(() => this.facade.trialColor());

  readonly label = computed(() => {
    const days = this.facade.trialDaysLeft();
    if (days == null || days <= 0) return this.i18n.t('billing.trial.badgeExpired');
    return this.i18n.t('billing.trial.badge', { days });
  });

  readonly billingLink = computed(() => {
    const key = this.tenant.companyKey();
    if (!key) return ['/'];
    return ['/c', key, 'settings', 'billing'];
  });
}
